import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import AuthService from '../services/authService';
import { onUnauthorized } from '../services/authEvents';

const AuthContext = createContext();

const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  LOGOUT: 'LOGOUT',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_USER: 'SET_USER',
  INIT_DONE: 'INIT_DONE',
};

// isLoading starts true so ProtectedRoute shows a spinner during the
// initial token-check instead of flashing a redirect to /login.
const initialState = {
  user: null,
  userType: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isLoginLoading: false,
  isRegisterLoading: false,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return { ...state, isLoading: true, isLoginLoading: true, error: null };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        userType: action.payload.userType,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        isLoginLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isLoginLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.REGISTER_START:
      return { ...state, isLoading: true, isRegisterLoading: true, error: null };

    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return { ...state, isLoading: false, isRegisterLoading: false, error: null };

    case AUTH_ACTIONS.REGISTER_FAILURE:
      return { ...state, isLoading: false, isRegisterLoading: false, error: action.payload };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    // Initial token check completed — no stored token found.
    case AUTH_ACTIONS.INIT_DONE:
      return { ...state, isLoading: false };

    // Stored token found on startup.
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        userType: action.payload.userType,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Restore session from storage on mount.
  useEffect(() => {
    const token = AuthService.getToken();
    const userType = AuthService.getUserType();
    if (token && userType) {
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: { token, userType } });
    } else {
      dispatch({ type: AUTH_ACTIONS.INIT_DONE });
    }
  }, []);

  // Any 401/403 from either HTTP client fires fireUnauthorized().
  // We only dispatch LOGOUT here — no navigate() call.
  // ProtectedRoute's <Navigate to="/login"> handles the redirect AFTER
  // the auth state has already committed, avoiding the race condition where
  // LoginPage sees isAuthenticated:true and bounces the user back.
  useEffect(() => {
    const unsubscribe = onUnauthorized(() => {
      AuthService.logout();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      window.location.replace('/login');
    });
    return unsubscribe;
  }, []);

  const login = async (credentials, rememberMe = false) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    const result = await AuthService.login(credentials, rememberMe);
    if (result.success) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { userType: result.data.user_type, token: result.data.access_token },
      });
      return { success: true, userType: result.data.user_type };
    } else {
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: result.error });
      return { success: false, error: result.error };
    }
  };

  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });
    const result = await AuthService.register(userData);
    if (result.success) {
      dispatch({ type: AUTH_ACTIONS.REGISTER_SUCCESS });
      return { success: true, data: result.data };
    } else {
      dispatch({ type: AUTH_ACTIONS.REGISTER_FAILURE, payload: result.error });
      return { success: false, error: result.error };
    }
  };

  // Clears auth state. ProtectedRoute's <Navigate> handles the redirect.
  const logout = useCallback(() => {
    AuthService.logout();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  const value = {
    ...state,
    login,
    register,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
